import * as THREE from "three";

// Textures drawn on a canvas at load: nothing to fetch, so the game also runs
// as one self-contained file. Colours are the kitchen's own, not the UI
// palette's: quarry tile, subway tile, paper.

function canvasTexture(w: number, h: number, draw: (g: CanvasRenderingContext2D) => void, repeat?: [number, number]): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  draw(canvas.getContext("2d")!);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  if (repeat) {
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(...repeat);
  }
  return tex;
}

/** A small, stable pseudo-random sequence, so the tiles look the same every load. */
function speckle(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return s / 2147483647;
  };
}

/** Red quarry tile, 15 cm squares with dark grout: every diner kitchen floor. */
export function quarryTile(width: number, depth: number): THREE.CanvasTexture {
  return canvasTexture(
    256,
    256,
    (g) => {
      const rnd = speckle(7);
      g.fillStyle = "#3b2a24";
      g.fillRect(0, 0, 256, 256);
      for (let y = 0; y < 2; y++) {
        for (let x = 0; x < 2; x++) {
          const shade = 150 + Math.floor(rnd() * 25);
          g.fillStyle = `rgb(${shade}, ${Math.floor(shade * 0.42)}, ${Math.floor(shade * 0.3)})`;
          g.fillRect(x * 128 + 4, y * 128 + 4, 120, 120);
          for (let i = 0; i < 60; i++) {
            g.fillStyle = `rgba(0,0,0,${rnd() * 0.12})`;
            g.fillRect(x * 128 + 4 + rnd() * 118, y * 128 + 4 + rnd() * 118, 2, 2);
          }
        }
      }
    },
    [width / 0.3, depth / 0.3],
  );
}

/** White subway tile, 15 × 7.5 cm, running bond. */
export function subwayTile(width: number, height: number): THREE.CanvasTexture {
  return canvasTexture(
    256,
    128,
    (g) => {
      g.fillStyle = "#c9cfcf";
      g.fillRect(0, 0, 256, 128);
      for (let row = 0; row < 4; row++) {
        const off = row % 2 ? 64 : 0;
        for (let col = -1; col < 3; col++) {
          const x = col * 128 + off;
          const grad = g.createLinearGradient(0, row * 32, 0, row * 32 + 32);
          grad.addColorStop(0, "#fbfcfa");
          grad.addColorStop(1, "#eceeea");
          g.fillStyle = grad;
          g.fillRect(x + 2, row * 32 + 2, 124, 28);
        }
      }
    },
    [width / 0.3, height / 0.15],
  );
}

/** The sign over every restaurant sink. */
export function handWashSign(): THREE.CanvasTexture {
  return canvasTexture(256, 160, (g) => {
    g.fillStyle = "#fdfdf8";
    g.fillRect(0, 0, 256, 160);
    g.strokeStyle = "#1d4f91";
    g.lineWidth = 8;
    g.strokeRect(4, 4, 248, 152);
    g.fillStyle = "#1d4f91";
    g.textAlign = "center";
    g.font = "bold 26px sans-serif";
    g.fillText("EMPLOYEES", 128, 44);
    g.fillText("MUST WASH HANDS", 128, 78);
    g.font = "bold 18px sans-serif";
    g.fillText("BEFORE RETURNING", 128, 112);
    g.fillText("TO WORK", 128, 136);
  });
}

/** The chalkboard by the pass with the morning's specials. */
export function specialsBoard(name: string, specials: string[]): THREE.CanvasTexture {
  return canvasTexture(256, 192, (g) => {
    g.fillStyle = "#26302b";
    g.fillRect(0, 0, 256, 192);
    g.fillStyle = "#f2efe4";
    g.textAlign = "center";
    g.font = "bold 22px cursive, sans-serif";
    g.fillText(name, 128, 36);
    g.font = "18px cursive, sans-serif";
    g.fillStyle = "#f6d77c";
    g.fillText("Today's specials", 128, 66);
    g.fillStyle = "#f2efe4";
    g.font = "16px cursive, sans-serif";
    specials.forEach((line, i) => g.fillText(line, 128, 98 + i * 24));
  });
}

/** A ticket slip for the rail: the item, and the dishes it needs. */
export function ticketTexture(item: string, dishes: string): THREE.CanvasTexture {
  return canvasTexture(128, 160, (g) => {
    g.fillStyle = "#fffdf2";
    g.fillRect(0, 0, 128, 160);
    g.fillStyle = "#c33";
    g.fillRect(0, 0, 128, 10);
    g.fillStyle = "#222";
    g.font = "bold 15px monospace";
    const words = item.split(" ");
    let line = "";
    let y = 36;
    for (const w of words) {
      if ((line + w).length > 12) {
        g.fillText(line.trim(), 8, y);
        y += 20;
        line = "";
      }
      line += `${w} `;
    }
    g.fillText(line.trim(), 8, y);
    g.font = "12px monospace";
    g.fillStyle = "#555";
    g.fillText(dishes, 8, 140);
  });
}

/** Black-and-white checkerboard tile, 30 cm squares: a burger joint's floor. */
export function checkerTile(width: number, depth: number): THREE.CanvasTexture {
  return canvasTexture(
    128,
    128,
    (g) => {
      for (let y = 0; y < 2; y++) {
        for (let x = 0; x < 2; x++) {
          g.fillStyle = (x + y) % 2 ? "#f1eee6" : "#23252a";
          g.fillRect(x * 64, y * 64, 64, 64);
        }
      }
      g.strokeStyle = "rgba(0,0,0,0.25)";
      g.lineWidth = 2;
      g.strokeRect(0, 0, 128, 128);
    },
    [width / 0.6, depth / 0.6],
  );
}

/** A strip of labels, one per topping bin, for the front of the cold rail. */
export function binLabels(names: string[]): THREE.CanvasTexture {
  return canvasTexture(64 * names.length, 32, (g) => {
    g.fillStyle = "#2b2d42";
    g.fillRect(0, 0, 64 * names.length, 32);
    g.fillStyle = "#f8f4e8";
    g.font = "bold 13px sans-serif";
    g.textAlign = "center";
    names.forEach((n, i) => g.fillText(n.toUpperCase(), 32 + i * 64, 21));
  });
}

/** The shack's painted menu board. */
export function menuBoard(title: string, lines: string[]): THREE.CanvasTexture {
  return canvasTexture(256, 192, (g) => {
    g.fillStyle = "#b8321f";
    g.fillRect(0, 0, 256, 192);
    g.fillStyle = "#fbe6b0";
    g.fillRect(8, 8, 240, 176);
    g.fillStyle = "#b8321f";
    g.textAlign = "center";
    g.font = "bold 24px sans-serif";
    g.fillText(title.toUpperCase(), 128, 40);
    g.fillStyle = "#2b2d42";
    g.font = "bold 15px sans-serif";
    lines.forEach((line, i) => g.fillText(line, 128, 74 + i * 24));
  });
}

/** Weathered boardwalk planks laid across the walk, 15 cm wide. `length` is how far the walk runs. */
export function boardwalkPlanks(length: number): THREE.CanvasTexture {
  return canvasTexture(
    256,
    64,
    (g) => {
      const rnd = speckle(31);
      const tones = ["#a77b4f", "#9a6f45", "#b08657", "#94693f"];
      for (let i = 0; i < 16; i++) {
        g.fillStyle = tones[Math.floor(rnd() * tones.length)]!;
        g.fillRect(i * 16, 0, 16, 64);
        g.fillStyle = "rgba(60,38,20,0.55)";
        g.fillRect(i * 16, 0, 1.5, 64);
        g.fillStyle = "rgba(40,25,12,0.6)";
        g.fillRect(i * 16 + 7, 6 + rnd() * 4, 2, 2);
        g.fillRect(i * 16 + 7, 54 - rnd() * 4, 2, 2);
      }
    },
    [length / 2.4, 1],
  );
}

/** Pale beach sand with a little speckle. */
export function sandTexture(): THREE.CanvasTexture {
  return canvasTexture(
    128,
    128,
    (g) => {
      const rnd = speckle(11);
      g.fillStyle = "#e8d5a6";
      g.fillRect(0, 0, 128, 128);
      for (let i = 0; i < 500; i++) {
        g.fillStyle = rnd() > 0.5 ? "rgba(190,160,110,0.35)" : "rgba(255,248,225,0.4)";
        g.fillRect(rnd() * 128, rnd() * 128, 1.5, 1.5);
      }
    },
    [120, 10],
  );
}

/** A summer sky, deep blue overhead to hazy at the horizon, for the inside of a dome. */
export function skyGradient(): THREE.CanvasTexture {
  return canvasTexture(4, 256, (g) => {
    const grad = g.createLinearGradient(0, 0, 0, 256);
    grad.addColorStop(0, "#3d8fd6");
    grad.addColorStop(0.42, "#9fd0f0");
    grad.addColorStop(0.5, "#cfe9f7");
    grad.addColorStop(1, "#cfe9f7");
    g.fillStyle = grad;
    g.fillRect(0, 0, 4, 256);
  });
}

/** The ordering kiosk's screen: the menu as big tiles to tap. */
export function kioskScreen(): THREE.CanvasTexture {
  return canvasTexture(128, 96, (g) => {
    g.fillStyle = "#f8f4e8";
    g.fillRect(0, 0, 128, 96);
    g.fillStyle = "#d8392b";
    g.fillRect(0, 0, 128, 20);
    g.fillStyle = "#ffffff";
    g.font = "bold 13px sans-serif";
    g.textAlign = "center";
    g.fillText("ORDER HERE", 64, 15);
    const tiles = ["#e9a23b", "#c0532e", "#e9a23b", "#5fa83a", "#c0532e", "#e9a23b"];
    tiles.forEach((c, i) => {
      g.fillStyle = c;
      g.fillRect(8 + (i % 3) * 40, 28 + Math.floor(i / 3) * 32, 32, 26);
    });
  });
}

/** The soda machine's labels, one per nozzle, each in its drink's colour. */
export function sodaLabels(names: string[]): THREE.CanvasTexture {
  const colors = ["#7fc4e8", "#5a2e1a", "#a9744a", "#d9d94a"];
  return canvasTexture(96 * names.length, 28, (g) => {
    names.forEach((n, i) => {
      g.fillStyle = colors[i % colors.length]!;
      g.fillRect(i * 96 + 3, 0, 90, 28);
      g.fillStyle = i === 0 || i === 3 ? "#1f2433" : "#fdf6e3";
      g.font = "bold 15px sans-serif";
      g.textAlign = "center";
      g.fillText(n.toUpperCase(), i * 96 + 48, 19);
    });
  });
}
